/**
 * The Surface primitive.
 *
 * Cards, banners, callouts and pills are one shell with three variables — not
 * four components. Getting that right before any screen exists is what stops
 * every screen re-implementing the same box slightly differently.
 *
 * The rules from `components.md` are encoded here as **refusals**, because a
 * rule that lives only in a document is a rule the twentieth component quietly
 * breaks. Two are compile errors and one is a dev-time throw; which mechanism
 * each gets is explained at the point it is enforced.
 */
import { createContext, createElement, useContext, } from "react";
/**
 * Tones that count as "toned" for the nesting rule.
 *
 * `components.md` says *"a toned Surface may not contain another toned
 * Surface"* without defining toned, so this is a **reading** and is flagged as
 * one: `neutral` and `inert` are excluded because neither reports a condition.
 * `inert` is the grey of draft, expired, archived and tombstoned — it marks the
 * *absence* of active state, and a draft row sitting inside a warning banner is
 * an ordinary composition rather than the competing-signals problem the rule
 * exists to prevent. If the design means all six, this set is the one line to
 * change.
 */
const TONED = new Set([
    "live",
    "warn",
    "error",
    "recall",
]);
/** The tone of the nearest enclosing Surface, for the nesting refusal. */
const EnclosingTone = createContext(null);
/**
 * The default emphasis, which depends on the size — RULED 2026-08-11 (Q2).
 *
 * This was a flat `emphasis = "flat"` for every size, and it was the single
 * most expensive default in the product: `flat` resolves to
 * `background-color: transparent`, so **every card on every screen rendered
 * canvas-grey while every mock rendered a white card on a grey canvas**. It
 * held the whole app together with a 1px border and was a large part of why the
 * product read washed out against the handoff.
 *
 * The handoff never said what an unspecified Surface does — `components.md`
 * specified tone and size thoroughly and skipped this — so the wrong answer was
 * not contradicted anywhere. Now it is a table (`components.md` → Surface →
 * Defaults): `card` and `pill` fill, `inline` stays flat *because it exists for
 * blocks already sitting on a white surface*, which is the reasoning that makes
 * the exception a rule rather than an exemption.
 *
 * Worth keeping in view: one default in one primitive decides the appearance of
 * every screen. That is the Surface bet — "cards, banners, callouts and pills
 * are one component" — paying out in the direction nobody wants, and it is why
 * this is ruled once here rather than per screen.
 */
function defaultEmphasisFor(size) {
    return size === "inline" ? "flat" : "filled";
}
export function Surface({ tone = "neutral", emphasis, size = "card", sectioned = false, as = "div", children, className, ...rest }) {
    const enclosing = useContext(EnclosingTone);
    const resolvedEmphasis = emphasis ?? defaultEmphasisFor(size);
    /**
     * A toned Surface inside a toned Surface — a dev-time throw.
     *
     * A throw rather than a warning because a `console.warn` in a component tree
     * is indistinguishable from the twenty other lines a dev server prints, and
     * this is a rule about whether a user can tell what the screen is telling
     * them. Dev-time rather than always, because breaking a rendered dashboard in
     * production over a styling rule trades a legibility problem for an outage.
     *
     * The remedy is in the message and comes from `components.md` verbatim: the
     * OUTER one becomes neutral. Nesting the tones is what makes both meaningless.
     */
    if (process.env.NODE_ENV !== "production" &&
        enclosing !== null &&
        TONED.has(enclosing) &&
        TONED.has(tone)) {
        throw new Error(`Surface: a "${tone}" Surface cannot sit inside a "${enclosing}" Surface. ` +
            `Two toned surfaces nested make neither readable as a signal — on a healthy ` +
            `dashboard exactly one thing is coloured. If you need a warning inside a ` +
            `warning, make the OUTER one tone="neutral".`);
    }
    return createElement(EnclosingTone.Provider, { value: tone }, createElement(as, {
        ...rest,
        className: className === undefined ? "pp-surface" : `pp-surface ${className}`,
        "data-tone": tone,
        "data-emphasis": resolvedEmphasis,
        "data-size": size,
        "data-sectioned": sectioned ? "true" : undefined,
    }, children));
}
export function BillingSurface(props) {
    return createElement(Surface, props);
}
/**
 * A sectioned card's header row — a sentence and an optional meta, over a rule.
 *
 * `components.md` → Attention card: *"The title is a sentence that answers the question,
 * never a section label."* That is a copy rule the component cannot enforce, so it is
 * stated here where the next person writing a title will read it.
 *
 * The meta is `--pp-text-muted` and not the mock's `--pp-text-faint`: the mock's value is
 * about 3.5:1 on white, below the 4.5:1 floor, and it is the same token on the same kind
 * of line that the identity block had to correct. Deciding it in the primitive settles it
 * for every card header rather than once per screen.
 */
export function SurfaceHeader({ title, meta, }) {
    return createElement("div", { className: "pp-surface__header" }, createElement("div", { className: "pp-surface__header-title" }, title), meta === undefined
        ? null
        : createElement("div", { className: "pp-surface__header-meta" }, meta));
}
/**
 * A sectioned card's body.
 *
 * `bleed` removes the padding so rows carrying their own fill and dividers reach the
 * card's edges — otherwise a divider is a line with gaps at both ends, which reads as a
 * mistake rather than a separator.
 */
export function SurfaceBody({ bleed = false, children, }) {
    return createElement("div", { className: "pp-surface__body", "data-bleed": bleed ? "true" : undefined }, children);
}
/** A sectioned card's sunken footer — the quiet line under a rule. */
export function SurfaceFooter({ children }) {
    return createElement("div", { className: "pp-surface__footer" }, children);
}
//# sourceMappingURL=Surface.js.map